<template>
  <SectionContentGuideTopicCard v-bind="props" />
</template>

<script>
const sexualHealthColumnsDefault = [
  {
    imageSrc: '/assets/images/sexual-health-blood-flow.jpg',
    imageAlt: 'Red blood cells flowing through a vessel',
    title: 'Blood Flow',
    content: `<p>While testosterone is a hormonal driver of men's sexual health, blood flow is considered to be the primary physical driver, specifically regarding both achieving and maintaining erections.</p>
<p>When blood flow is hindered, this is often due to vascular issues such as high blood pressure, clogged arteries, obesity, high cholesterol, diabetes, sedentary lifestyle, diet, etc.</p>
<p>Thankfully, increasing blood flow not only improves these symptoms, it also aids in increasing testosterone levels, essentially yielding a win-win.</p>
<p>Even better, increased blood flow also improves overall heart health, weight management, and insulin sensitivity.</p>
<p>Regular cardiovascular exercise, stress management, maintaining a healthy weight, quality sleep, and quitting smoking can all help to improve blood flow.</p>`
  },
  {
    imageSrc: '/assets/images/sexual-health-exercise.jpg',
    imageAlt: 'Man resting after exercise outdoors',
    title: 'Exercise',
    content: `<p>While we mentioned cardiovascular exercise above, as a means to improve blood flow pertaining to sexual health, exercise in and of itself also brings benefits.</p>
<p>Regular exercise is shown to not only improves testosterone levels and blood flow, it also:</p>
<ul>
  <li><u>Reduces stress</u> (lower stress levels lead to increased sexual desire and function)</li>
  <li><u>Improves body image</u> (better body image typically leads to better self esteem, and these both serve to increase sexual confidence and improve sexual experiences)</li>
  <li><u>Improves energy and stamina</u> (these improvements boost confidence and performance both in and out of the bedroom)<sup>27</sup></li>
</ul>`
  }
]

</script>

<script setup>
const props = defineProps({
  heroImageSrc: {
    type: String,
    default: '/assets/images/sexual-health-hero.jpg'
  },
  heroImageAlt: {
    type: String,
    default: 'Couple holding hands on white bedsheets'
  },
  heroImageHeight: {
    type: Number,
    default: 253
  },
  title: {
    type: String,
    default: 'Sexual Health'
  },
  titleSize: {
    type: String,
    default: 'medium'
  },
  introContent: {
    type: String,
    default: `<p>A lack of sexual desire, erectile dysfunction, and diminishing sperm count are common concerns in men, especially as they age.</p>
<p>Hormonally speaking, testosterone is one of the primary drivers of men's sexual health.</p>
<p>And, as these levels decline roughly 1% annually beginning at around age 30 in men, related concerns typically follow.<sup>26</sup></p>
<p>These concerns can be addressed, however, in the following ways:</p>`
  },
  sections: {
    type: Array,
    default: () => []
  },
  columns: {
    type: Array,
    default: () => sexualHealthColumnsDefault
  },
  contentGap: {
    type: String,
    default: 'medium'
  },
  paddingTop: {
    type: String,
    default: 'reduced'
  },
  paddingBottom: {
    type: String,
    default: 'reduced'
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Topic Info Image Card',
  inputs: [
    {
      name: 'heroImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/sexual-health-hero.jpg'
    },
    {
      name: 'heroImageAlt',
      type: 'text',
      defaultValue: 'Couple holding hands on white bedsheets'
    },
    {
      name: 'heroImageHeight',
      type: 'number',
      defaultValue: 253
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Sexual Health'
    },
    {
      name: 'titleSize',
      type: 'text',
      enum: ['large', 'medium'],
      defaultValue: 'medium'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>A lack of sexual desire, erectile dysfunction, and diminishing sperm count are common concerns in men, especially as they age.</p>
<p>Hormonally speaking, testosterone is one of the primary drivers of men's sexual health.</p>
<p>And, as these levels decline roughly 1% annually beginning at around age 30 in men, related concerns typically follow.<sup>26</sup></p>
<p>These concerns can be addressed, however, in the following ways:</p>`
    },
    {
      name: 'sections',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'type',
          type: 'text',
          enum: ['divider', 'split', 'tags'],
          defaultValue: 'split'
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'content',
          type: 'richText',
          defaultValue: ''
        },
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'imagePosition',
          type: 'text',
          enum: ['left', 'right'],
          defaultValue: 'left'
        },
        {
          name: 'imageHeight',
          type: 'number',
          defaultValue: 350
        },
        {
          name: 'tagsText',
          type: 'longText',
          defaultValue: '',
          helperText: 'For tags sections: one tag per line'
        }
      ]
    },
    {
      name: 'columns',
      type: 'list',
      defaultValue: [
        {
          imageSrc: '/assets/images/sexual-health-blood-flow.jpg',
          imageAlt: 'Red blood cells flowing through a vessel',
          title: 'Blood Flow',
          content: `<p>While testosterone is a hormonal driver of men's sexual health, blood flow is considered to be the primary physical driver, specifically regarding both achieving and maintaining erections.</p>
<p>When blood flow is hindered, this is often due to vascular issues such as high blood pressure, clogged arteries, obesity, high cholesterol, diabetes, sedentary lifestyle, diet, etc.</p>
<p>Thankfully, increasing blood flow not only improves these symptoms, it also aids in increasing testosterone levels, essentially yielding a win-win.</p>
<p>Even better, increased blood flow also improves overall heart health, weight management, and insulin sensitivity.</p>
<p>Regular cardiovascular exercise, stress management, maintaining a healthy weight, quality sleep, and quitting smoking can all help to improve blood flow.</p>`
        },
        {
          imageSrc: '/assets/images/sexual-health-exercise.jpg',
          imageAlt: 'Man resting after exercise outdoors',
          title: 'Exercise',
          content: `<p>While we mentioned cardiovascular exercise above, as a means to improve blood flow pertaining to sexual health, exercise in and of itself also brings benefits.</p>
<p>Regular exercise is shown to not only improves testosterone levels and blood flow, it also:</p>
<ul>
  <li><u>Reduces stress</u> (lower stress levels lead to increased sexual desire and function)</li>
  <li><u>Improves body image</u> (better body image typically leads to better self esteem, and these both serve to increase sexual confidence and improve sexual experiences)</li>
  <li><u>Improves energy and stamina</u> (these improvements boost confidence and performance both in and out of the bedroom)<sup>27</sup></li>
</ul>`
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'content',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'contentGap',
      type: 'text',
      enum: ['large', 'medium'],
      defaultValue: 'medium'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
